type HeadingListener = (heading: number | null) => void;

/** A native compass feed. Resolves to an unsubscribe. */
export type NativeHeadingSource = (
  handler: (reading: { trueHeading: number; magneticHeading: number }) => void,
) => Promise<() => void | Promise<void>>;

export type DeviceHeadingOptions = {
  /** Core Location's compass, when the app has one (iOS). Preferred outright:
   * it needs no permission of its own and says which north it means. */
  nativeSource?: NativeHeadingSource | null;
};
type StateListener = () => void;

/** How long to wait before deciding a silent compass means a missing
 * permission. Only ever consulted when NOT ONE orientation event has arrived —
 * see initialize(). */
const COMPASS_ACQUIRE_TIMEOUT_MS = 1200;
const COMPASS_GRACEFUL_TIMEOUT_MS = 3000;
const HEADING_SMOOTH_ALPHA = 0.15;

/**
 * Why this exists: across the whole 2026-08-23 Red Rocks run, every
 * proximity.compass.state sample on both phones read ready:false, needsTap:false,
 * denied:false, and no transition was ever logged. Both branches of initialize()
 * should produce a transition within 3 s, so something upstream is not firing — most
 * likely DeviceOrientation events never arrive in Capacitor's WKWebView. These counters
 * make the next device run answer that directly instead of by inference.
 */
export type DeviceHeadingDiagnostics = {
  /** Which initialize() path ran. 'native' is Core Location's compass; the
   * other two are the web DeviceOrientation fallback. */
  branch: 'native' | 'requestPermission' | 'direct' | 'uninitialized';
  /** True once a window listener is attached. */
  listening: boolean;
  /** DeviceOrientation events received, including ones with no usable heading. */
  eventCount: number;
  /** Events that yielded a finite heading. */
  usableEventCount: number;
  lastEventAt: number | null;
};

export interface DeviceHeadingController {
  getCurrent(): number | null;
  isReady(): boolean;
  needsTap(): boolean;
  isPermissionDenied(): boolean;
  getDiagnostics(): DeviceHeadingDiagnostics;
  subscribe(listener: HeadingListener): () => void;
  onStateChange(listener: StateListener): () => void;
  initialize(): void;
  requestPermission(): Promise<boolean>;
  dispose(): void;
}

export async function requestDeviceOrientationPermission(): Promise<boolean> {
  const DOE = DeviceOrientationEvent as typeof DeviceOrientationEvent & {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  };

  if (typeof DOE.requestPermission === 'function') {
    try {
      const result = await DOE.requestPermission();
      return result === 'granted';
    } catch {
      return true;
    }
  }

  return true;
}

export function createDeviceHeading(options: DeviceHeadingOptions = {}): DeviceHeadingController {
  const nativeSource = options.nativeSource ?? null;
  let nativeUnsub: (() => void | Promise<void>) | null = null;
  const listeners = new Set<HeadingListener>();
  const stateListeners = new Set<StateListener>();
  let current: number | null = null;
  let smoothedSin = 0;
  let smoothedCos = 0;
  let hasFirstReading = false;
  let started = false;
  let disposed = false;
  let compassReady = false;
  let needsTapForPermission = false;
  let permissionDenied = false;
  let initTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let initBranch: DeviceHeadingDiagnostics['branch'] = 'uninitialized';
  let eventCount = 0;
  let usableEventCount = 0;
  let lastEventAt: number | null = null;
  /** Smooth and publish a TRUE-north heading in degrees. Shared by the native
   * compass and the DeviceOrientation fallback. */
  function publishHeading(rawDeg: number): void {
    if (!Number.isFinite(rawDeg)) {
      return;
    }
    usableEventCount += 1;
    const heading = ((rawDeg % 360) + 360) % 360;

    if (!compassReady) {
      markCompassReady();
    }

    const radians = (heading * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);

    if (!hasFirstReading) {
      smoothedSin = sin;
      smoothedCos = cos;
      hasFirstReading = true;
    } else {
      smoothedSin = HEADING_SMOOTH_ALPHA * sin + (1 - HEADING_SMOOTH_ALPHA) * smoothedSin;
      smoothedCos = HEADING_SMOOTH_ALPHA * cos + (1 - HEADING_SMOOTH_ALPHA) * smoothedCos;
    }

    const smoothedRad = Math.atan2(smoothedSin, smoothedCos);
    current = ((smoothedRad * 180) / Math.PI + 360) % 360;
    listeners.forEach((cb) => cb(current));
  }

  /** Web fallback. On iOS this is superseded by Core Location — see initialize(). */
  function handleEvent(event: DeviceOrientationEvent): void {
    eventCount += 1;
    lastEventAt = Date.now();

    if (typeof event.webkitCompassHeading === 'number') {
      // Treated as TRUE heading. WebKit fills it from CLHeading.trueHeading
      // when Core Location can supply one, and the engine used to add WMM2020
      // declination on top — a second correction. It was invisible for years
      // because the radar draws peers RELATIVE to your heading, so rotating
      // every bearing by the same amount reads as nothing; the map's cone over
      // a north-up basemap was the first surface to show it.
      //
      // This path is now the FALLBACK. iOS reads CLHeading.trueHeading
      // directly, which states its reference instead of leaving it to be
      // inferred — the inference is what got this wrong twice.
      publishHeading(event.webkitCompassHeading);
    } else if (typeof event.alpha === 'number') {
      publishHeading(360 - event.alpha);
    }
  }

  function clearInitTimeout(): void {
    if (initTimeoutId !== null) {
      clearTimeout(initTimeoutId);
      initTimeoutId = null;
    }
  }

  function markCompassReady(): void {
    if (compassReady) {
      return;
    }
    compassReady = true;
    needsTapForPermission = false;
    clearInitTimeout();
    stateListeners.forEach((cb) => cb());
  }

  function start(): void {
    if (started) {
      return;
    }
    const win = window as Window & { ondeviceorientationabsolute?: unknown };
    if (win.ondeviceorientationabsolute !== undefined) {
      window.addEventListener('deviceorientationabsolute', handleEvent as EventListener);
    } else {
      window.addEventListener('deviceorientation', handleEvent as EventListener);
    }
    started = true;
  }

  return {
    initialize() {
      clearInitTimeout();

      // Core Location's compass first, and exclusively when it exists.
      //
      // The web DeviceOrientation API costs a permission the user has to TAP
      // for, and WKWebView does not persist it — so the app asked on every
      // single launch and the tap bought nothing a granted location
      // authorisation did not already cover. CLHeading rides that
      // authorisation, so there is nothing to ask and nothing to tap.
      if (nativeSource) {
        initBranch = 'native';
        started = true;
        needsTapForPermission = false;
        void nativeSource((reading) => {
          eventCount += 1;
          lastEventAt = Date.now();
          // trueHeading is -1 until Core Location has a fix to derive
          // declination from; magneticHeading is always valid. Preferring the
          // stated true heading is the whole point of this source.
          const deg = reading.trueHeading >= 0 ? reading.trueHeading : reading.magneticHeading;
          publishHeading(deg);
        })
          .then((unsub) => {
            nativeUnsub = unsub;
            if (disposed) {
              void unsub();
            }
          })
          .catch(() => {
            // A native compass that will not start is not a permission problem
            // the user can fix, so no prompt — the cone simply never appears
            // and the diagnostics say listening:false.
            started = false;
            initBranch = 'uninitialized';
            stateListeners.forEach((cb) => cb());
          });
        return;
      }

      const DOE = DeviceOrientationEvent as typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<'granted' | 'denied'>;
      };

      if (typeof DOE.requestPermission === 'function') {
        initBranch = 'requestPermission';
        start();
        initTimeoutId = setTimeout(() => {
          initTimeoutId = null;
          if (compassReady) {
            return;
          }
          // SILENCE is the permission signature, not slowness.
          //
          // This used to prompt whenever no usable heading had arrived yet,
          // which conflated two unrelated things. Orientation events start
          // immediately once permission exists, but the HEADING inside them is
          // absent until Core Location has a magnetometer fix — seconds, and
          // longer near metal or a magnetic case. So a granted, perfectly
          // healthy compass got a "tap to start" prompt every time, and the tap
          // did nothing but dismiss it. With no events at all, the listener is
          // genuinely being starved and the permission really is the reason.
          if (eventCount > 0) {
            return;
          }
          needsTapForPermission = true;
          stateListeners.forEach((cb) => cb());
        }, COMPASS_ACQUIRE_TIMEOUT_MS);
        return;
      }

      initBranch = 'direct';
      start();
      initTimeoutId = setTimeout(() => {
        initTimeoutId = null;
        if (!compassReady) {
          markCompassReady();
        }
      }, COMPASS_GRACEFUL_TIMEOUT_MS);
    },

    async requestPermission() {
      const DOE = DeviceOrientationEvent as typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<'granted' | 'denied'>;
      };

      if (typeof DOE.requestPermission === 'function') {
        try {
          const result = await DOE.requestPermission();
          if (result === 'granted') {
            permissionDenied = false;
            needsTapForPermission = false;
            start();
            stateListeners.forEach((cb) => cb());
            return true;
          }
          if (result === 'denied') {
            permissionDenied = true;
            needsTapForPermission = false;
            stateListeners.forEach((cb) => cb());
            return false;
          }
          return false;
        } catch {
          start();
          needsTapForPermission = false;
          stateListeners.forEach((cb) => cb());
          return true;
        }
      }

      start();
      return true;
    },

    getDiagnostics() {
      return {
        branch: initBranch,
        listening: started,
        eventCount,
        usableEventCount,
        lastEventAt,
      };
    },

    isReady() {
      return compassReady;
    },

    needsTap() {
      return needsTapForPermission;
    },

    isPermissionDenied() {
      return permissionDenied;
    },

    onStateChange(listener) {
      stateListeners.add(listener);
      return () => {
        stateListeners.delete(listener);
      };
    },

    getCurrent() {
      return current;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    dispose() {
      disposed = true;
      clearInitTimeout();
      if (nativeUnsub) {
        void nativeUnsub();
        nativeUnsub = null;
      }
      window.removeEventListener('deviceorientationabsolute', handleEvent as EventListener);
      window.removeEventListener('deviceorientation', handleEvent as EventListener);
      listeners.clear();
      stateListeners.clear();
      started = false;
      compassReady = false;
      needsTapForPermission = false;
      permissionDenied = false;
      current = null;
      hasFirstReading = false;
    },
  };
}
